import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import MenuBar from "../components/MenuBar";
import { db } from "../firebase";
import { motion, AnimatePresence } from "framer-motion";
import HeaderBar from "../components/HeaderBar";
import toast, { Toaster } from "react-hot-toast";

/** Firestore / legacy imports often use lowercase keys; this UI uses camelCase. */
function normalizePartyDoc(id, raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  const ot = String(d.openingType ?? d.openingtype ?? "Dr").trim();
  let openingType = ot;
  const ol = ot.toLowerCase();
  if (ol === "c" || ol === "cr" || ol === "credit") openingType = "Cr";
  else if (ol === "d" || ol === "dr" || ol === "debit") openingType = "Dr";

  return {
    id,
    ...d,
    partyName: String(d.partyName ?? d.partyname ?? d.name ?? "").trim(),
    group: String(d.group ?? "Sundry Creditors").trim() || "Sundry Creditors",
    openingBalance: d.openingBalance ?? d.openingbalance ?? "",
    openingType,
    dateOfOpening: d.dateOfOpening ?? d.dateofopening ?? "",
    maintainBillWise: String(d.maintainBillWise ?? d.maintainbillwise ?? "Yes").trim() || "Yes",
    address: String(d.address ?? "").trim(),
    city: String(d.city ?? "").trim(),
    state: String(d.state ?? "").trim(),
    pin: d.pin != null && d.pin !== "" ? String(d.pin) : "",
    gst: String(d.gst ?? "").trim(),
    phone: String(d.phone ?? "").trim(),
    email: String(d.email ?? "").trim(),
    contactPerson: String(d.contactPerson ?? d.contactperson ?? "").trim(),
    creditDays: d.creditDays ?? d.creditdays ?? "",
    creditLimit: d.creditLimit ?? d.creditlimit ?? "",
    creditRating: String(d.creditRating ?? d.creditrating ?? "").trim(),
    pan: String(d.pan ?? "").trim(),
    aadhaar: String(d.aadhaar ?? "").trim(),
    remarks: String(d.remarks ?? "").trim(),
    inactive: String(d.inactive ?? "No").trim() || "No",
  };
}

// Tab button
const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
      active
        ? "bg-slate-900 text-white shadow-md"
        : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
    }`}
  >
    {children}
  </button>
);

// Action button
const NeoButton = ({ children, color, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${color} hover:opacity-95 active:scale-[0.98] transition`}
  >
    {children}
  </button>
);

export default function PartyCreation() {
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({
    id: "",
    partyName: "",
    group: "Sundry Creditors",
    openingBalance: "",
    openingType: "Dr",   // 🔹 added
    dateOfOpening: "",
    maintainBillWise: "Yes",
    address: "",
    city: "",
    state: "",
    pin: "",
    gst: "",
    phone: "",
    email: "",
    contactPerson: "",
    creditDays: "",
    creditLimit: "",
    creditRating: "",
    pan: "",
    aadhaar: "",
    remarks: "",
    inactive: "No",
  });

  const [showFind, setShowFind] = useState(false);
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState("");

  const fetchParties = async () => {
    const querySnapshot = await getDocs(collection(db, "parties"));
    const data = querySnapshot.docs.map((docSnap) => normalizePartyDoc(docSnap.id, docSnap.data()));
    setParties(data);
  };

  const handleSave = async () => {
    if (!formData.partyName.trim())
      return toast.error("Party Name is required!");

    const dataToSave = { ...formData };
    delete dataToSave.id;
    for (const k of [
      "partyname",
      "openingtype",
      "maintainbillwise",
      "dateofopening",
      "openingbalance",
      "contactperson",
      "creditdays",
      "creditlimit",
      "creditrating",
    ]) {
      delete dataToSave[k];
    }

    try {
      if (formData.id) {
        await updateDoc(doc(db, "parties", formData.id), dataToSave);
        toast.success("Party updated!");
      } else {
        await addDoc(collection(db, "parties"), dataToSave);
        toast.success("Party added!");
      }
      resetForm();
      fetchParties();
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return toast.error("Select a party first!");
    if (!window.confirm("Are you sure to delete this Party?")) return;
    try {
      await deleteDoc(doc(db, "parties", formData.id));
      toast.success("Deleted successfully!");
      resetForm();
      fetchParties();
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const resetForm = () => {
    setFormData({
      id: "",
      partyName: "",
      group: "Sundry Creditors",
      openingBalance: "",
      openingType: "Dr",   // reset also
      dateOfOpening: "",
      maintainBillWise: "Yes",
      address: "",
      city: "",
      state: "",
      pin: "",
      gst: "",
      phone: "",
      email: "",
      contactPerson: "",
      creditDays: "",
      creditLimit: "",
      creditRating: "",
      pan: "",
      aadhaar: "",
      remarks: "",
      inactive: "No",
    });
  };

  useEffect(() => {
    fetchParties();
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <MenuBar />
      <Toaster position="top-right" />
      <HeaderBar />

      <div className="p-2 md:p-4 w-full">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3 md:p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Masters</p>
                <h1 className="mt-1 text-lg md:text-xl font-bold text-slate-900">Party Creation</h1>
                <p className="text-[11px] md:text-xs text-slate-600 mt-1">
                  Create and maintain party ledger accounts with contact and credit controls.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] min-w-[190px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                  <p className="text-slate-500">Total Parties</p>
                  <p className="text-sm font-bold text-slate-900">{parties.length}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                  <p className="text-slate-500">Editing</p>
                  <p className="text-sm font-bold text-slate-900">{formData.id ? "Yes" : "No"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
          <TabButton
            active={activeTab === "general"}
            onClick={() => setActiveTab("general")}
          >
            General
          </TabButton>
          <TabButton
            active={activeTab === "contacts"}
            onClick={() => setActiveTab("contacts")}
          >
            Contacts
          </TabButton>
          <TabButton
            active={activeTab === "credit"}
            onClick={() => setActiveTab("credit")}
          >
            Credit Limit
          </TabButton>
          <TabButton
            active={activeTab === "others"}
            onClick={() => setActiveTab("others")}
          >
            Others
          </TabButton>
          </div>

          {/* Tab Content */}
          <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-3 md:p-4">
          {activeTab === "general" && (
            <Section title="General Information">
              <InputField
                label="Party Name"
                value={formData.partyName}
                onChange={(v) => handleChange("partyName", v)}
                icon="Name"
              />
              <SelectField
                label="Group"
                value={formData.group}
                options={["Sundry Creditors", "Sundry Debtors", "Others"]}
                onChange={(v) => handleChange("group", v)}
              />
              <InputField
                label="Opening Balance"
                type="number"
                value={formData.openingBalance}
                onChange={(v) => handleChange("openingBalance", v)}
                icon="Rs"
              />
              <SelectField
                label="Opening Type"
                value={formData.openingType}
                options={["Dr", "Cr"]}
                onChange={(v) => handleChange("openingType", v)}
              />
              <InputField
                label="Date of Opening"
                type="date"
                value={formData.dateOfOpening}
                onChange={(v) => handleChange("dateOfOpening", v)}
                icon="Date"
              />
              <SelectField
                label="Maintain Bill Wise"
                value={formData.maintainBillWise}
                options={["Yes", "No"]}
                onChange={(v) => handleChange("maintainBillWise", v)}
              />
            </Section>
          )}

          {activeTab === "contacts" && (
            <Section title="Contact Details">
              <InputField
                label="Address"
                value={formData.address}
                onChange={(v) => handleChange("address", v)}
                icon="Addr"
              />
              <InputField
                label="City"
                value={formData.city}
                onChange={(v) => handleChange("city", v)}
                icon="City"
              />
              <InputField
                label="State"
                value={formData.state}
                onChange={(v) => handleChange("state", v)}
                icon="State"
              />
              <InputField
                label="PIN Code"
                value={formData.pin}
                onChange={(v) => handleChange("pin", v)}
                icon="PIN"
              />
              <InputField
                label="GST"
                value={formData.gst}
                onChange={(v) => handleChange("gst", v)}
                icon="GST"
              />
              <InputField
                label="Phone"
                value={formData.phone}
                onChange={(v) => handleChange("phone", v)}
                icon="Tel"
              />
              <InputField
                label="Email"
                type="text"
                value={formData.email}
                onChange={(v) => handleChange("email", v)}
                icon="Mail"
                placeholder="one@supplier.com or sales@x.com, owner@x.com"
              />
              <InputField
                label="Contact Person"
                value={formData.contactPerson}
                onChange={(v) => handleChange("contactPerson", v)}
                icon="PIC"
              />
            </Section>
          )}

          {activeTab === "credit" && (
            <Section title="Credit Details">
              <InputField
                label="Credit Days"
                type="number"
                value={formData.creditDays}
                onChange={(v) => handleChange("creditDays", v)}
                icon="Days"
              />
              <InputField
                label="Credit Limit"
                type="number"
                value={formData.creditLimit}
                onChange={(v) => handleChange("creditLimit", v)}
                icon="Limit"
              />
              <SelectField
                label="Credit Rating"
                value={formData.creditRating}
                options={["Excellent", "Good", "Average", "Poor"]}
                onChange={(v) => handleChange("creditRating", v)}
              />
            </Section>
          )}

          {activeTab === "others" && (
            <Section title="Other Information">
              <InputField
                label="PAN No."
                value={formData.pan}
                onChange={(v) => handleChange("pan", v)}
                icon="PAN"
              />
              <InputField
                label="Aadhaar No."
                value={formData.aadhaar}
                onChange={(v) => handleChange("aadhaar", v)}
                icon="ID"
              />
              <TextareaField
                label="Remarks"
                value={formData.remarks}
                onChange={(v) => handleChange("remarks", v)}
              />
              <SelectField
                label="Inactive"
                value={formData.inactive}
                options={["No", "Yes"]}
                onChange={(v) => handleChange("inactive", v)}
              />
            </Section>
          )}

          {/* Actions */}
          <div className="border-t border-slate-200 mt-4 pt-3 flex flex-wrap gap-2 justify-end">
            <NeoButton color="bg-white text-slate-700 border border-slate-300" onClick={resetForm}>
              New
            </NeoButton>
            <NeoButton color="bg-blue-600 text-white" onClick={() => setShowFind(true)}>
              Find
            </NeoButton>
            <NeoButton color="bg-emerald-600 text-white" onClick={handleSave}>
              {formData.id ? "Update Party" : "Save Party"}
            </NeoButton>
            <NeoButton color="bg-rose-600 text-white" onClick={handleDelete}>
              Delete
            </NeoButton>
          </div>
        </div>

        {/* Find Popup */}
        <AnimatePresence>
          {showFind && (
            <FindPopup
              parties={parties}
              search={search}
              setSearch={setSearch}
              onSelect={(p) => {
                setFormData(p);
                setShowFind(false);
              }}
              onClose={() => setShowFind(false)}
            />
          )}
        </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ---------- Reusable Components ---------- */
const Section = ({ title, children }) => (
  <div className="mb-4">
    <h2 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const InputField = ({ label, type = "text", value, onChange, icon, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    <div className="relative">
      {icon && (
        <span className="absolute left-2 top-2 rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[9px] font-semibold text-slate-500">
          {icon}
        </span>
      )}
      <input
        type={type}
        placeholder={placeholder}
        className="border border-slate-300 h-8 w-full pl-12 pr-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-slate-800/20 bg-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  </div>
);

const SelectField = ({ label, value, options, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-slate-300 h-8 w-full px-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-slate-800/20 bg-white"
    >
      {options.map((opt) => (
        <option key={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const TextareaField = ({ label, value, onChange }) => (
  <div className="col-span-1 md:col-span-2">
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    <textarea
      rows="2"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-slate-300 p-2 w-full rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-slate-800/20 bg-white"
    />
  </div>
);

function partyMatchesSearch(p, q) {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return true;
  const name = String(p.partyName ?? p.partyname ?? p.name ?? "").toLowerCase();
  const phone = String(p.phone ?? "").toLowerCase();
  const email = String(p.email ?? "").toLowerCase();
  const gst = String(p.gst ?? "").toLowerCase();
  const city = String(p.city ?? "").toLowerCase();
  return (
    name.includes(needle) ||
    phone.includes(needle) ||
    email.includes(needle) ||
    gst.includes(needle) ||
    city.includes(needle)
  );
}

// Popup with animation
const FindPopup = ({ parties, search, setSearch, onSelect, onClose }) => {
  const filtered = parties.filter((p) => partyMatchesSearch(p, search));
  return (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-50 p-4"
  >
    <motion.div
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.8 }}
      className="bg-white p-3 md:p-4 rounded-xl w-full max-w-3xl max-h-[80vh] overflow-auto shadow-2xl border border-slate-200"
    >
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-base text-slate-900">Select Party</h3>
        <button onClick={onClose} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900">
          Close
        </button>
      </div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search name, phone, email, GST, city…"
        className="border border-slate-300 h-8 px-2 w-full mb-3 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-slate-800/20 bg-white"
      />
      <div className="overflow-auto">
        <table className="w-full border border-slate-200 border-collapse text-xs">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="p-2 border border-slate-200 text-left">Party Name</th>
              <th className="p-2 border border-slate-200 text-left">Phone</th>
              <th className="p-2 border border-slate-200 text-left">Email</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
                <tr
                  key={p.id}
                  onClick={() => onSelect(p)}
                  className={`cursor-pointer hover:bg-indigo-50 ${
                    i % 2 === 0 ? "bg-white" : "bg-slate-50"
                  }`}
                >
                  <td className="p-2 border border-slate-200">{p.partyName || "—"}</td>
                  <td className="p-2 border border-slate-200">{p.phone || "-"}</td>
                  <td className="p-2 border border-slate-200">{p.email || "-"}</td>
                </tr>
              ))}
            {filtered.length === 0 && (
              <tr>
                <td className="p-4 border border-slate-200 text-center text-slate-500" colSpan={3}>
                  No parties found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </motion.div>
  </motion.div>
  );
};
